import TinyColor from "@ctrl/tinycolor";
import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import { KeyboardEvent, memo, useRef, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoBadge, RedoBadgeSize } from "../badge/redo-badge";
import {
  BrandKitColor,
  RedoColorPicker,
  formatColorOutput,
} from "./redo-color-picker";
import * as css from "./redo-color-picker.module.css";

export const RedoColorPickerBadge = memo(function RedoColorPickerBadge({
  value,
  onChange,
  disabled = false,
  label,
  showAlpha = true,
  overlayPicker,
  size = "sm",
  brandKitColors,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
  label?: string;
  showAlpha?: boolean;
  overlayPicker?: { shouldUse: boolean; anchorOverride?: HTMLElement | null };
  size?: RedoBadgeSize;
  brandKitColors?: BrandKitColor[];
}) {
  const anchorRef = useRef<HTMLElement>(null);

  const [open, setOpen] = useState(false);

  const handleClick = (event: { stopPropagation(): void }) => {
    if (disabled) {
      setOpen(false);
      return;
    }
    setOpen(!open);
    event.stopPropagation();
  };

  const onClickAway = useHandler(() => {
    setOpen(false);
  });

  const handleKeyDown = useHandler((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      anchorRef.current?.focus();
    }
  });

  //convert "transparent" to hex with preserved RGB values
  const normalizedValue =
    value === "transparent" && showAlpha
      ? (() => {
          // try to get previous RGB  or default to black
          const previousColor = TinyColor("#000000");
          const rgb = previousColor.toRgb();
          return TinyColor({
            r: rgb.r,
            g: rgb.g,
            b: rgb.b,
            a: 0,
          }).toHex8String();
        })()
      : value;

  return (
    <Flex dir="column" gap="xs">
      {label && <Text fontSize="xs">{label}</Text>}
      <span
        className={css.badge}
        onBlur={(e) => {
          const relatedTarget = e.relatedTarget as HTMLElement | null;
          // If relatedTarget is null (clicking non-focusable element), let ClickAwayListener handle it
          // Only close if focus is explicitly moving to something outside the picker
          if (
            !relatedTarget ||
            relatedTarget.closest("[data-redo-color-picker]")
          ) {
            return;
          }
          setOpen(false);
        }}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.preventDefault();
            setOpen(false);
          } else if (e.key === "Enter" || e.key === " ") {
            handleClick(e);
          }
        }}
        ref={anchorRef}
        tabIndex={disabled ? -1 : 0}
      >
        <RedoBadge
          color="gray"
          monospace
          segmentLeading={
            value === "transparent"
              ? undefined
              : { type: "color", color: value }
          }
          size={size}
          text={
            value === "transparent"
              ? "TRANSPARENT"
              : (() => {
                  const color = TinyColor(value);
                  return color.isValid
                    ? formatColorOutput(color, showAlpha).toUpperCase()
                    : value.toUpperCase();
                })()
          }
        />
      </span>
      {open && (
        <ClickAwayListener onClickAway={onClickAway}>
          {overlayPicker?.shouldUse ? (
            <Popper
              anchorEl={overlayPicker.anchorOverride ?? anchorRef.current}
              open={open}
            >
              <div data-redo-color-picker onKeyDown={handleKeyDown}>
                <RedoColorPicker
                  brandKitColors={brandKitColors}
                  onChange={onChange}
                  showAlpha={showAlpha}
                  value={normalizedValue}
                />
              </div>
            </Popper>
          ) : (
            <div data-redo-color-picker onKeyDown={handleKeyDown}>
              <RedoColorPicker
                brandKitColors={brandKitColors}
                onChange={onChange}
                showAlpha={showAlpha}
                value={normalizedValue}
              />
            </div>
          )}
        </ClickAwayListener>
      )}
    </Flex>
  );
});
